import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import type { CampaignTask } from '@bvengineering/cas-member-gateway-service-client';
import {
  BannerCounter,
  Button,
  Divider,
  IconButton,
  Link,
  List,
  Typography,
} from '@/primer/components';
import type { ListItemProps } from '@/primer/components';
import { PageColumn, PageLoadingState } from '@/components/layout';
import {
  ContentRequirements,
  TaskConfirmationModal,
  OptOutConfirmationModal,
  ReservationTimerBanner,
  ReservationSlowDownModal,
} from '@/components/ui';
import { useCampaign, useCampaignGuidance } from '@/api';
import { useIsProviderConnected } from '@/api/queries/settings/useQuerySocialConnections';
import { SocialProvider } from '@/api/queries/settings/constants';
import { hasActiveReservation, useReservationExpiry } from '@/hooks/useReservationTimer';
import { getTaskDisplay, isSupportedTaskType } from '@/utils/taskTypes';
import { MS_PER_DAY, findReservedUserProduct, hasClaimedProduct, pluralize } from '@/utils';
import {
  normalizeTaskState,
  isTaskCompleted,
  isTaskValidating,
  TASK_STATE,
  TASK_VALIDATING_MESSAGE,
  TASK_DISABLED_MESSAGE,
} from '@/utils/taskState';
import { parseTaskRejectionReason } from '@/utils/taskRejectionReasons';

const SUPPORT_EMAIL = 'creators@influenster.com';

/** Builds the state-driven list row for a deliverable task. */
function deliverableRowItem(
  task: CampaignTask,
  openTask: () => void,
  hasClaimed: boolean
): ListItemProps {
  const display = getTaskDisplay(task.type);
  const canOpen = isSupportedTaskType(task.type);
  const base = { icon: display.icon, title: display.name };

  switch (normalizeTaskState(task.state)) {
    case TASK_STATE.COMPLETED:
      return { ...base, trailingIcon: 'circle-tick-filled', trailingIconColor: 'success' };
    case TASK_STATE.VALIDATING:
      return { ...base, subtitle: TASK_VALIDATING_MESSAGE, loading: true };
    case TASK_STATE.REJECTED: {
      const rejectionMessage = parseTaskRejectionReason(task.stateReason, task.type).message;
      if (!canOpen) {
        return {
          ...base,
          subtitle: rejectionMessage,
          subtitleColor: 'error',
          disabled: true,
        };
      }
      return {
        ...base,
        subtitle: rejectionMessage,
        subtitleColor: 'error',
        trailingIcon: 'chevron-right',
        trailingIconColor: 'light',
        onClick: openTask,
      };
    }
    case TASK_STATE.PERFORMABLE:
      if (canOpen) {
        return {
          ...base,
          trailingIcon: 'chevron-right',
          trailingIconColor: 'light',
          onClick: openTask,
        };
      }
      return {
        ...base,
        subtitle: TASK_DISABLED_MESSAGE,
        trailingIcon: 'chevron-right',
        disabled: true,
      };
    // Pre-claim: locked because reviews above haven't been done — show the message.
    // Post-claim with no stateReason: locked only because the product hasn't shipped — show nothing.
    // Post-claim with a stateReason (e.g. unfinished review): show the message.
    case TASK_STATE.DISABLED:
    default:
      return {
        ...base,
        subtitle: !hasClaimed || task.stateReason ? TASK_DISABLED_MESSAGE : undefined,
        trailingIcon: 'chevron-right',
        disabled: true,
      };
  }
}

/**
 * Review hub shown after Select Product: the creator reviews the required tasks
 * and content requirements before continuing.
 */
export function CampaignTasks() {
  const { id: campaignId } = useParams({ from: '/_authenticated/campaign/$id/tasks' });
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(campaignId);
  const { data: guidanceData, isLoading: isGuidanceLoading } = useCampaignGuidance(campaignId);
  const {
    isConnected: isInstagramConnected,
    username: instagramUsername,
    isLoading: isConnectionLoading,
    isError: isConnectionError,
  } = useIsProviderConnected(SocialProvider.INSTAGRAM);
  const reservedUserProduct = findReservedUserProduct(campaign?.userProducts);
  const reservationExpired = useReservationExpiry(reservedUserProduct?.reservedDate ?? '');

  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showContentSheet, setShowContentSheet] = useState(false);
  const [tasksReviewed, setTasksReviewed] = useState(false);
  const [contentReviewed, setContentReviewed] = useState(false);
  const [showOptOutConfirm, setShowOptOutConfirm] = useState(false);

  if (isLoading) {
    return <PageLoadingState />;
  }

  if (error || !campaign) {
    return (
      <PageColumn className="text-center py-8">
        <div className="flex flex-col items-center gap-4">
          <Typography variant="header" size="h1" color="error" as="h1">
            Program Not Found
          </Typography>
          <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Home</Button>
        </div>
      </PageColumn>
    );
  }

  const tasks = campaign.tasks ?? [];

  const totalTaskCount = campaign.totalTaskCount;
  const completedTaskCount = campaign.completedTaskCount;
  // The countdown only makes sense while there is something left to complete.
  const allTasksDone = (totalTaskCount ?? 0) > 0 && (completedTaskCount ?? 0) >= totalTaskCount;

  const daysLeft = campaign.expired
    ? Math.max(0, Math.ceil((new Date(campaign.expired).getTime() - Date.now()) / MS_PER_DAY))
    : null;

  // Once the product is claimed the review step is over: Opt out and Next no longer apply.
  const hasClaimed = hasClaimedProduct(campaign.userProducts);
  const needsProductSelection = !hasClaimed && !hasActiveReservation(reservedUserProduct);

  const hasTasks = tasks.length > 0;
  const hasGuidance = isGuidanceLoading || !guidanceData || (guidanceData.results?.length ?? 0) > 0;
  const canContinue = (!hasTasks || tasksReviewed) && (!hasGuidance || contentReviewed);

  // Once every post is submitted or verified, the review rows are done and no longer open.
  const deliverables = tasks.filter((task) => isSupportedTaskType(task.type));
  const postsSubmitted =
    deliverables.length > 0 &&
    deliverables.every((task) => isTaskValidating(task.state) || isTaskCompleted(task.state));
  const tasksChecked = tasksReviewed || postsSubmitted;
  const contentChecked = contentReviewed || postsSubmitted;

  const reviewItems: ListItemProps[] = [
    ...(hasTasks
      ? [
          {
            icon: 'tasks',
            title: 'Required tasks',
            subtitle: "View what's needed to get your free product(s).",
            trailingIcon: tasksChecked ? 'circle-tick-filled' : 'chevron-right',
            trailingIconColor: tasksChecked ? 'success' : 'light',
            onClick: postsSubmitted ? undefined : () => setShowTaskModal(true),
          } satisfies ListItemProps,
        ]
      : []),
    ...(hasGuidance
      ? [
          {
            icon: 'image-tick',
            title: 'Content requirements',
            subtitle: 'Tips, guides and inspiration for creating your best content.',
            trailingIcon: contentChecked ? 'circle-tick-filled' : 'chevron-right',
            trailingIconColor: contentChecked ? 'success' : 'light',
            onClick: postsSubmitted ? undefined : () => setShowContentSheet(true),
          } satisfies ListItemProps,
        ]
      : []),
    ...tasks.map(
      (task): ListItemProps =>
        deliverableRowItem(
          task,
          () =>
            navigate({
              to: '/campaign/$id/tasks/$taskId',
              params: { id: campaignId, taskId: String(task.id) },
            }),
          hasClaimed
        )
    ),
  ];

  const handleTasksReviewed = () => {
    setTasksReviewed(true);
    setShowTaskModal(false);
  };

  const handleContentReviewed = () => {
    setContentReviewed(true);
    setShowContentSheet(false);
  };

  const handleNext = () => {
    if (needsProductSelection) {
      navigate({ to: '/campaign/$id/select-product', params: { id: campaignId }, replace: true });
      return;
    }
    // Wait for the connection status to resolve before gating — the hook reports
    // isConnected=false while /settings/social is still loading, which would
    // otherwise misroute an already-connected creator to the connect screen.
    // Treat a fetch error the same way: don't gate on isConnected=false when the
    // query failed, as the creator may already be connected.
    if (isConnectionLoading || isConnectionError) return;
    if (!hasClaimed && !hasActiveReservation(reservedUserProduct)) {
      navigate({ to: '/campaign/$id/select-product', params: { id: campaignId }, replace: true });
      return;
    }
    // Instagram must be connected before continuing; gate to the connect screen.
    if (!isInstagramConnected) {
      navigate({ to: '/campaign/$id/connect', params: { id: campaignId } });
      return;
    }
    if (hasClaimed) {
      navigate({ to: '/dashboard' });
      return;
    }
    navigate({ to: '/campaign/$id/address', params: { id: campaignId } });
  };

  const handleBack = () => {
    if (hasClaimed) {
      navigate({ to: '/dashboard' });
      return;
    }
    navigate({ to: '/campaign/$id/select-product', params: { id: campaignId }, replace: true });
  };

  return (
    <PageColumn className="py-4 flex flex-col gap-6">
      {reservedUserProduct?.reservedDate && (
        <>
          <ReservationTimerBanner
            campaignId={campaignId}
            reservedDate={reservedUserProduct.reservedDate}
          />
          {!reservationExpired && <ReservationSlowDownModal />}
        </>
      )}
      <div className="flex items-center gap-3">
        <IconButton icon="chevron-left" onClick={handleBack} aria-label="Go back" />
        <Typography variant="header" size="h1" as="h1">
          {campaign.name ?? 'Your program'}
        </Typography>
      </div>

      {!allTasksDone && (
        <BannerCounter count={`${completedTaskCount}/${totalTaskCount}`}>
          {daysLeft != null ? (
            <>
              <Typography
                variant="body"
                size="medium"
                color="info"
                as="span"
                decoration={{ bold: true }}
              >
                {daysLeft}
              </Typography>{' '}
              {pluralize(daysLeft, 'day')} left to complete your tasks
            </>
          ) : (
            'Complete your tasks to get your free product.'
          )}
        </BannerCounter>
      )}

      <div className="flex flex-col gap-4">
        <Typography variant="header" size="h2" as="h2">
          Please review the following:
        </Typography>

        <List shadow="first" items={reviewItems} />
      </div>

      <div className="flex flex-col gap-2">
        <Typography variant="header" size="h2" as="h2">
          Questions about this collab?
        </Typography>
        <Typography variant="body" size="medium" color="bodylight">
          If something doesn't look right or you have questions about the requirements, reach out to
          our team at <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </div>

      {!hasClaimed && (
        <>
          <Divider />

          <div className="flex gap-3">
            <Button
              variant="secondary"
              size="large"
              fullWidth
              onClick={() => setShowOptOutConfirm(true)}
            >
              Opt out
            </Button>
            <Button
              variant="primary"
              size="large"
              fullWidth
              onClick={handleNext}
              disabled={
                !canContinue ||
                (!needsProductSelection && (isConnectionLoading || isConnectionError))
              }
            >
              Next
            </Button>
          </div>
        </>
      )}

      <TaskConfirmationModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        onConfirm={handleTasksReviewed}
        tasks={tasks}
        handle={instagramUsername}
      />

      <ContentRequirements
        campaignId={campaignId}
        isOpen={showContentSheet}
        onClose={() => setShowContentSheet(false)}
        onAcknowledge={handleContentReviewed}
      />

      <OptOutConfirmationModal
        isOpen={showOptOutConfirm}
        onClose={() => setShowOptOutConfirm(false)}
        campaignId={campaignId}
      />
    </PageColumn>
  );
}

export default CampaignTasks;
